/**
 * 계약 상품 수정 컴포넌트
 * 계약의 상품 정보를 수정하는 컴포넌트입니다.
 * 상품명, 보증금, 상품유형, 계약금, 프로바이더 정보 등을 표시하고 수정할 수 있습니다.
 */

import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { DetailTotalData } from "src/api/contract/contract-types";
import { ProviderDetailModel } from "src/api/provider/provider-types";
import { BaseButton } from "src/components";
import Link from "src/components/PartnerLink";
import { ArticleSection } from "src/components/layout/ArticleSection";
import BaseSection from "src/components/layout/BaseSection";
import pagePath from "src/pagePath.json";
import SearchSpaceProductPopup from "src/pages/commonPopup/SearchSpaceProductPopup";
import { globalPartnerState } from "src/recoil/partners/atom";
import { numberToStringWithComma } from "src/utils";
import useContactApi from "../../hooks/useContractApi";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import { ProductModel } from "src/api/product/product-types";

/**
 * 계약 상품 수정 컴포넌트의 props 인터페이스
 */
interface ContractBasicProp {
  saveObject: any; // 저장 객체 업데이트 함수
  saveData: any; // 저장할 데이터
  product: ProductModel; // 상품 상세 정보
  setProduct: any; // 상품 상세 정보 설정 함수
  showYn?: boolean; // 표시 여부
  // onChangeContractRequest: (productId: number) => void; // 상품 변경 함수
}

/**
 * 계약 상품 수정 컴포넌트
 * @param detailData 상품 상세 조회 정보
 * @param saveData 저장할 데이터
 * @param saveObject 저장 객체 업데이트 함수
 * @param product 상품 상세 정보
 * @param setProduct 상품 상세 정보 설정 함수
 * @param showYn 표시 여부
 */
const ContractProductEdit = ({
  saveData,
  saveObject,
  product,
  setProduct,
  showYn, //
}: // onChangeContractRequest,
ContractBasicProp) => {
  const { getProductData, getProviderData } = useContactApi();
  const partner = useRecoilValue(globalPartnerState); // 파트너 정보
  const [usePrice, setUsePrice] = useState(""); // 이용 가격
  const [providerDetail, setProviderDetail] = useState<ProviderDetailModel>(); // 프로바이더 상세 정보
  const [pflag, setPflag] = useState(false); // 상품 검색 팝업 표시 여부

  /**
   * 상품 상세 페이지 URL 생성
   * @param id 상품 ID
   * @returns 상품 상세 페이지 URL
   */
  const goProduct = (id: number) => {
    let path = "";
    if (id) {
      path = pagePath.product.detail.replace(":id", id!.toString());
    }
    return path;
  };

  /**
   * 프로바이더 정보 조회
   * @param providerId 프로바이더 ID
   */
  const findProvider = useCallback(
    async (providerId: number) => {
      const pd = await getProviderData(providerId);
      setProviderDetail(pd);
    },
    [getProviderData],
  );

  /**
   * 상품 변경 처리
   * @param product 선택된 상품 정보
   */
  const onChangeProduct = useCallback(
    async (product: any) => {
      const findedProduct = await getProductData(product.id);
      setProduct(findedProduct);
      findProvider(findedProduct.providerId);
      const temp = _.cloneDeep(saveData);
      console.log("saveData", saveData);
      temp.productId = product!.id;
      temp.useTerm = 0; // 기간 초기화
      temp.useStartTime = ""; // 이용 시작일 초기화
      temp.useEndTime = ""; // 이용 종료일 초기화
      temp.selectedFacility = undefined; // 공용공간 초기화
      saveObject(temp);
      setPflag(false);
    },
    [findProvider, getProductData, saveData, saveObject, setProduct],
  );

  // 상품 상세 정보가 변경될 때마다 내부 상태 업데이트
  useEffect(() => {
    let price = "";
    if (product && Number(product?.productionPrice) < 0) {
      price = numberToStringWithComma(Number(product?.productionPrice) + Number(product?.productionPrice! / 10));
    } else {
      price = "0";
    }
    setUsePrice(price);
  }, [product?.productionPrice]);

  return (
    <ArticleSection
      title={"상품 정보"}
      noHashLink={showYn ? true : false}
      children={
        <>
          <div className="flex-center">
            <BaseSection
              title={"상품명"}
              children={
                <div className="contents-container__grid-contents">
                  <div className="flex-row flex-center-start w-100 font14">
                    {product?.productName} (id:{" "}
                    <Link to={goProduct(product?.id)} className="text-underline" target="_blank" rel="noopener noreferrer">
                      {product?.id}
                    </Link>
                    )
                    <div className="minmax90 ml10">
                      <BaseButton
                        title={product?.id ? "변경" : "선택"}
                        className="color-white"
                        onClick={() => {
                          setPflag(true);
                        }}
                      />

                      <SearchSpaceProductPopup
                        isOpen={pflag}
                        partnerId={String(partner?.id)}
                        onClick={onChangeProduct}
                        onClose={() => setPflag(false)}
                      />
                    </div>
                  </div>
                </div>
              }
            />
            {product?.productType !== "TIME_COURT" && (
              <BaseSection
                title={"보증금"}
                children={
                  <div className="contents-container__grid-contents">
                    <p>{product?.deposit ? numberToStringWithComma(Number(product?.deposit)) + " 원" : "-"}</p>
                  </div>
                }
              />
            )}
          </div>
          <div className="flex-center">
            <BaseSection title={"상품유형"} children={<ProductTypeChangeLabel productType={product?.productType} />} />

            {product?.productType !== "TIME_COURT" && (
              <BaseSection
                title={"ㄴ 계약금"}
                children={
                  <div className="contents-container__grid-contents">
                    <p>{product?.earnest ? numberToStringWithComma(Number(product?.earnest)) + " 원" : "-"}</p>
                  </div>
                }
              />
            )}
          </div>
          <div className="flex-center">
            <BaseSection
              title={"프로바이더 명"}
              children={
                <div className="contents-container__grid-contents">
                  {product?.providerId ? (
                    <div>
                      <p>{providerDetail?.provider?.providerName}</p>
                    </div>
                  ) : (
                    <p>-</p>
                  )}
                </div>
              }
            />

            {product?.productType !== "TIME_COURT" && (
              <BaseSection
                title={"ㄴ 잔금"}
                children={
                  <div className="contents-container__grid-contents">
                    <p>{product?.deposit ? numberToStringWithComma(Number(product?.deposit) - Number(product?.earnest)) + " 원" : "-"}</p>
                  </div>
                }
              />
            )}
          </div>

          {product?.productType === "TIME_COURT" && (
            <>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>승인 유형</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span>{product?.approvalType === "AUTO" ? "자동승인" : "관리자승인"}</span>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>이용신청서</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span>{product?.isUseApplication ? "사용" : "미사용"}</span>
                  </div>
                </div>
              </section>
            </>
          )}

          <div className="flex-center">
            {/* 상품타입 */}
            <BaseSection title={""} children={""} />

            {product?.productType !== "TIME_COURT" && (
              <BaseSection
                titleChildren={
                  <div className="contents-container__grid-index">
                    <p>
                      월 이용료
                      <br />
                      (부가세 포함 / 원)
                    </p>
                  </div>
                }
                children={
                  <div className="contents-container__grid-contents">
                    <p>{usePrice + " 원"}</p>
                  </div>
                }
              />
            )}
          </div>
        </>
      }
    />
  );
};

export default ContractProductEdit;
